import { bindAcquisition } from "@/lib/analytics/acquisition";
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from "react";
import { getSupabaseClient, isHttpOnlyRefreshEnabled, withTimeout, restoreLiveTabSession, invalidateLiveTabSession, isRemoteTabResetting, acceptsLiveTabSession } from "@/lib/supabase/client";
import {
  adoptRefreshToken,
  PROXY_REFRESH_PLACEHOLDER,
  probeSession,
  refreshAccessToken,
  proxyLogout,
  proxyLogoutStrict,
} from "@/lib/supabase/httpOnlyProxy";
import { queryClient } from "@/lib/queryClient";
import { safeRedirectPath } from "@/lib/safeRedirect";
import { canRecoverInlineCheckout } from "@/lib/checkout-entry";
import { webOAuthCallbackUrl } from "@/lib/web-oauth-callback";
import { enterRecoveryMode, isRecoveryMode } from "@/lib/auth/recoveryMode";
import { AuthContext, type UserProfile, type AuthState, type SoftDeletedInfo } from "@/hooks/useUser";
import type { User, Session } from "@supabase/supabase-js";
import { beginGuestCheckout } from "@/lib/guest-checkout-api";
import { getPlatformConfig } from "@/lib/platform/config";
import { DRAFT_SIGNOUT_KEY_PREFIXES } from "@/hooks/useLocalDraft";
import {
  driverInvitationLoginPath,
  DRIVER_INVITATION_CONTINUATION_KEY,
} from "@/lib/driverInvitationContinuation";
import { Sentry } from "@/lib/sentry";
import { identify as analyticsIdentify, reset as analyticsReset } from "@/lib/analytics";
import { connectAccountConsent, disconnectAccountConsent } from "@/lib/analytics/account-consent";
import { bootIntercom, shutdownIntercom, updateIntercom, type BootTraits } from "@/lib/intercom";
import { toast } from "@/hooks/use-toast";
import i18n from "@/i18n";
import {
  proxyAuthLogin,
  proxyAuthPasswordReset,
  proxyAuthRequestOtp,
  proxyAuthSignup,
  rateLimitedAuthError,
  reportOtpVerificationComplete,
} from "@/lib/auth-proxy";

/**
 * Task #3526 — decode the `aal` claim from a Supabase access token
 * WITHOUT verifying the signature (the token was just minted by
 * Supabase for this client, so we only need to read the claim, not
 * trust it as a security boundary — the server still re-checks aal on
 * every admin request). Mirrors the server's `decodeAal` in
 * routes/auth-mfa.ts. Used after an MFA verify to confirm the elevated
 * session actually carries aal2 before we drop the masking and adopt
 * the refresh token into the HttpOnly cookie.
 */
function decodeAccessTokenAal(token: string | null | undefined): "aal1" | "aal2" | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = JSON.parse(
      decodeURIComponent(
        atob(b64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      ),
    );
    const aal = json?.aal;
    return aal === "aal2" || aal === "aal1" ? aal : null;
  } catch {
    return null;
  }
}

/**
 * Centralised purge of every per-tab cache that is keyed off the *current*
 * signed-in user. Called from three hot paths:
 *
 *   - SIGNED_OUT (so the next signup/sign-in on this tab cannot read the
 *     departing user's profile or queries),
 *   - SIGNED_IN  (belt-and-braces: a previous session that ended without
 *     SIGNED_OUT — crash, hard reload mid-flow, OAuth bounce — can leave
 *     `ensure_user_cache:*` entries scoped to the *prior* auth UUID. Our
 *     new fetch is keyed off the new auth UUID so a stale entry won't be
 *     read directly, but other code paths and dev tools shouldn't see
 *     dead user data either),
 *   - explicit signOut().
 *
 * Single authoritative purge of every auth-owned key across both
 * sessionStorage AND localStorage. This is the *only* place that
 * decides what "auth state" lives in browser storage — every
 * sign-out, SIGNED_OUT, SIGNED_IN handler, and explicit
 * cross-account flow calls this. Adding a new auth-owned key means
 * adding it here.
 *
 * The contract:
 *   - sessionStorage:
 *       • `ensure_user_cache`                — legacy unscoped cache
 *       • `ensure_user_cache:<authUserId>`   — per-user profile cache
 *       • `auth_post_complete_next`          — legacy redirect token
 *       • `bac_post_auth_redirect`           — current redirect token
 *   - localStorage:
 *       • `auth_pending_signup_meta`         — terms / marketing choices
 *                                              captured before the email
 *                                              confirmation hop. MUST be
 *                                              dropped on sign-out so a
 *                                              second user on the same
 *                                              device cannot inherit
 *                                              user A's terms-acceptance
 *                                              metadata (#1752 review).
 *       • `sb-*`                             — Supabase client tokens.
 *                                              Supabase's own
 *                                              signOut({scope:"local"})
 *                                              also clears these, but we
 *                                              wipe them up-front so any
 *                                              code that runs in the
 *                                              gap between purge and
 *                                              signOut sees a clean tab.
 *
 * Why a single helper: prior to #1752 every sign-out path duplicated a
 * partial subset of these removals. The drift was the root cause of
 * defects 1 + 2; centralising prevents that drift from re-emerging.
 *
 * NOTE: `bac_support_consent_v1` (the functional Intercom support-consent
 * override, see lib/intercom.ts) is deliberately NOT in any of these lists.
 * It is browser-level functional state, not auth state, and must survive the
 * full-page reload that signOut performs so a user who engaged with support
 * keeps their Messenger + conversation history after logout/login. Do not add
 * it to the purge.
 */
// App-owned keys that hold *the previous user's* derived state. Safe to
// purge on SIGNED_IN — they cache server responses, nothing about the
// current Supabase session, the current sign-up's in-flight metadata,
// or the current sign-in's intended destination lives here.
export const APP_CACHE_KEY_PREFIXES = ["ensure_user_cache"] as const;
export const APP_CACHE_KEYS_EXACT = [] as const;

// Keys that must NOT be purged on SIGNED_IN because the value is written
// *around* the very SIGNED_IN event and consumed only after it.
//
// `auth_pending_signup_meta` carries terms / marketing choices the user
// made *before* the email-confirmation hop — wiping it on SIGNED_IN
// (which fires when the user clicks the confirmation link) would
// silently lose that metadata before it reaches the server (#1752
// review feedback).
//
// `bac_post_auth_redirect` / `auth_post_complete_next` (the post-auth
// landing path) are the in-modal OTP/password equivalent: AuthModal's
// `submitOtpCode` / `submitPassword` call `rememberPostAuthNext(next)`
// to stash the checkout (or other) destination immediately after the
// verify resolves, then `PostAuthRedirector` reads it back once the
// profile loads and `is_complete` is true. Supabase's SIGNED_IN
// listener fires *after* that write, so purging these here on SIGNED_IN
// deterministically wiped the destination — stranding the renter back on
// the car page after a Reserve→sign-in (Task #4693). They are written
// fresh on every sign-in attempt (`rememberPostAuthNext` clears them when
// there is no `next`) and consumed-then-removed by the redirector, so a
// stale value cannot survive a later sign-in. They are still wiped on
// full sign-out, where any pending redirect intent is moot.
//
// The driver-invitation account-switch continuation is also cleared by the
// full sign-out purge. Its dedicated helper keeps the validated destination
// in memory and navigates directly to the localized login URL after sign-out;
// the short-lived storage record remains only as a bounded recovery fallback
// for sessions created by older clients.
//
// OAuth / magic links carry `next` on the callback URL as their primary
// destination. Web OAuth also stashes it before leaving as a same-tab backup;
// the callback consumes that backup before exchanging the provider code.
export const AUTH_SIGNOUT_ONLY_KEYS_EXACT = [
  "auth_pending_signup_meta",
  "auth_post_complete_next",
  "bac_post_auth_redirect",
  "bac:checkout-post-auth-claim",
  DRIVER_INVITATION_CONTINUATION_KEY,
] as const;

// Supabase session shards. Only safe to purge on SIGNED_OUT — wiping
// these on SIGNED_IN would race the just-issued tokens out of
// localStorage and log the user out across reloads (#1752 review).
export const AUTH_SESSION_KEY_PREFIXES = ["sb-"] as const;

const INITIAL_ANONYMOUS_CONCIERGE_DRAFT_KEYS = [
  "bac:draft:concierge-request",
  "bac:draft:professional-inquiry",
] as const;

function matchesPrefix(k: string, prefixes: readonly string[]): boolean {
  return prefixes.some((p) => k === p || k.startsWith(p + ":") || k.startsWith(p));
}

// Reading the storage property itself can throw in Safari's restricted frames;
// guarding only getItem/length (or even using typeof localStorage) is too late.
function authStorage(name: "localStorage" | "sessionStorage"): Storage | null {
  try {
    return typeof window === "undefined" ? null : window[name] ?? null;
  } catch {
    return null;
  }
}

function hasPriorAuthenticatedStorageEvidence(): boolean {
  const prefixes = [...APP_CACHE_KEY_PREFIXES, ...AUTH_SESSION_KEY_PREFIXES];
  for (const name of ["localStorage", "sessionStorage"] as const) {
    const store = authStorage(name);
    // An inaccessible store is not proof of an anonymous, unused device.
    if (!store) return true;
    try {
      for (let i = 0; i < store.length; i++) {
        const key = store.key(i);
        if (key && matchesPrefix(key, prefixes)) return true;
      }
    } catch {
      // We cannot safely establish that there was no prior account session.
      return true;
    }
  }
  return false;
}

/**
 * The Supabase INITIAL_SESSION(null) callback is emitted during public-page
 * boot as well as a real signed-out transition. Preserve only anonymous or
 * pre-owner (legacy v1) concierge drafts when no auth/session artifacts point
 * to a prior account. UUID-owned drafts and every unrelated draft still get
 * purged by the normal auth cleanup.
 */
export function anonymousConciergeDraftKeysToPreserve(): string[] {
  if (hasPriorAuthenticatedStorageEvidence()) return [];
  const store = authStorage("localStorage");
  if (!store) return [];
  const preserve: string[] = [];
  for (const key of INITIAL_ANONYMOUS_CONCIERGE_DRAFT_KEYS) {
    try {
      const raw = store.getItem(key);
      if (!raw) continue;
      const envelope = JSON.parse(raw) as { data?: Record<string, unknown> } | null;
      if (!envelope?.data || typeof envelope.data !== "object") continue;
      const hasOwner = Object.prototype.hasOwnProperty.call(envelope.data, "ownerAuthId");
      if (!hasOwner || envelope.data.ownerAuthId === "anonymous") preserve.push(key);
    } catch {
      // Malformed data is not trusted enough to preserve.
    }
  }
  return preserve;
}

/** A cold anonymous bootstrap is not a logout. Retain only the current
 * unowned checkout's short-lived form; prior-account evidence denies this. */
export function anonymousCheckoutDraftKeysToPreserve(): string[] {
  if (hasPriorAuthenticatedStorageEvidence()) return [];
  try {
    if (!/^\/(?:[a-z]{2}\/){0,2}checkout\/?$/i.test(window.location.pathname)) return [];
    const flow = new URLSearchParams(window.location.search).get("h") || "";
    if (!/^[0-9a-f-]{36}$/i.test(flow)) return [];
    const store = authStorage("sessionStorage");
    if (!store) return [];
    const state = JSON.parse(store.getItem(`bac:checkout-secure:${flow}`) || "null");
    if (!state || state.ownerId) return [];
    const key = `bac:checkout-customer:${flow}`;
    const draft = JSON.parse(store.getItem(key) || "null");
    return draft?.expiresAt > Date.now() && draft?.data ? [key] : [];
  } catch { return []; }
}

function purgeStorageMatching(
  store: Storage,
  prefixes: readonly string[],
  exact: readonly string[],
  preserveExact: readonly string[] = [],
): void {
  try {
    const toDrop: string[] = [];
    for (let i = 0; i < store.length; i++) {
      const k = store.key(i);
      if (!k) continue;
      if (preserveExact.includes(k)) continue;
      if (exact.includes(k) || matchesPrefix(k, prefixes)) toDrop.push(k);
    }
    for (const k of toDrop) {
      try { store.removeItem(k); } catch { /* noop */ }
    }
  } catch { /* noop */ }
}

/**
 * SIGNED_IN-safe purge. Drops every key that holds *previous user*
 * derived state — user-scoped ensure-user caches, post-auth redirect
 * tokens, and the pending-signup metadata blob — but never touches the
 * Supabase session shards (`sb-*`). Calling this from SIGNED_IN is
 * essential to fix the cross-user leak (#1752 defect 2) without
 * wiping the freshly-issued tokens for the user who just signed in.
 */
export function clearUserScopedAppCaches(): void {
  for (const name of ["sessionStorage", "localStorage"] as const) {
    const store = authStorage(name);
    if (store) purgeStorageMatching(store, APP_CACHE_KEY_PREFIXES, APP_CACHE_KEYS_EXACT);
  }
}

/**
 * Full sign-out purge. Everything `clearUserScopedAppCaches` drops PLUS
 * Supabase's own `sb-*` session shards. Call from SIGNED_OUT,
 * explicit signOut(), and the expired-token recovery path. Do NOT
 * call from SIGNED_IN — that would log the user out the instant they
 * sign in.
 */
export function clearAllAuthStorage(options: { preserveExactKeys?: readonly string[] } = {}): void {
  // Task #7654 — long-text drafts (bac:draft:* / bac:review-draft:*) are
  // sign-out-only purges: they must survive SIGNED_IN (a login mid-draft
  // keeps the draft) but never leak to the next account on a shared device.
  const allPrefixes = [
    ...APP_CACHE_KEY_PREFIXES,
    ...(isRemoteTabResetting() ? [] : AUTH_SESSION_KEY_PREFIXES),
    ...DRAFT_SIGNOUT_KEY_PREFIXES,
    "bac:checkout-customer:",
  ];
  const allExact = [...APP_CACHE_KEYS_EXACT, ...AUTH_SIGNOUT_ONLY_KEYS_EXACT];
  for (const name of ["sessionStorage", "localStorage"] as const) {
    const store = authStorage(name);
    if (store) purgeStorageMatching(store, allPrefixes, allExact, options.preserveExactKeys);
  }
}

// Upper bound (ms) for any single post-login auth-client step
// (setSession / getSession / cookie adoption). The promise-chain lock in
// the Supabase client is the real deadlock fix; this is the safety net so
// that even if one of those calls hangs, the sign-in resolves and the
// modal's setLoading(false) always runs instead of spinning forever.
const AUTH_CLIENT_STEP_TIMEOUT_MS = 8000;

export async function completeDriverInvitationSignOut(
  continuationPath: string,
  options: {
    proxyLogout: () => Promise<void>;
    supabaseSignOut: () => Promise<{ error: Error | null }>;
    clearAuthState: () => void;
    baseUrl?: string;
    reload: (loginPath: string) => void;
  },
): Promise<void> {
  const loginPath = driverInvitationLoginPath(continuationPath, options.baseUrl);
  if (!loginPath) {
    throw new Error(i18n.t("booking.primary_driver.invitation_invalid"));
  }
  try {
    sessionStorage.removeItem(DRIVER_INVITATION_CONTINUATION_KEY);
  } catch {
    throw new Error(i18n.t("booking.primary_driver.invitation_switch_failed"));
  }

  // Do not clear local state or navigate until both remote sign-out
  // operations have completed successfully. These bounds keep the explicit
  // account-switch action recoverable if either service stalls.
  await withTimeout(
    options.proxyLogout(),
    AUTH_CLIENT_STEP_TIMEOUT_MS,
    "Driver invitation proxy sign-out",
  );
  const result = await withTimeout(
    options.supabaseSignOut(),
    AUTH_CLIENT_STEP_TIMEOUT_MS,
    "Driver invitation Supabase sign-out",
  );
  if (result.error) throw result.error;

  options.clearAuthState();
  options.reload(loginPath);
}

function otpSessionSetupError(): Error {
  return new Error(i18n.t("auth.session_setup_failed"));
}
function stalledAuthClientError(): Error & { code: string } {
  const e = new Error(
    "We signed you in but the session didn't finish setting up. Please try again.",
  ) as Error & { code: string };
  e.code = "auth_client_timeout";
  return e;
}

// Single source of truth for Intercom trait payloads — keeps every login
// path (getSession, onAuthStateChange, profile update) consistent.
function buildIntercomTraits(
  profile: UserProfile,
  email: string | undefined,
  isOwner: boolean,
  identity?: { userHash?: string | null; userJwt?: string | null } | null,
): BootTraits {
  const fullName = [profile.first_name, profile.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return {
    userId: profile.id,
    email: email || undefined,
    name: fullName || undefined,
    userHash: identity?.userHash || undefined,
    userJwt: identity?.userJwt || undefined,
    role: isOwner ? "host" : "renter",
    locale: profile.language || profile.locale || undefined,
    currency: profile.preferred_currency || profile.currency || undefined,
    signupDate: profile.created_at || undefined,
  };
}

async function fetchIntercomIdentity(
  accessToken: string,
): Promise<{ userHash: string | null; userJwt: string | null }> {
  try {
    const res = await fetch(
      `${import.meta.env.BASE_URL || "/"}api/users/me/intercom-hash`,
      { headers: { Authorization: `Bearer ${accessToken}` } },
    );
    if (!res.ok) return { userHash: null, userJwt: null };
    const { userHash, userJwt } = await res.json();
    return { userHash: userHash || null, userJwt: userJwt || null };
  } catch {
    return { userHash: null, userJwt: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const supabase = getSupabaseClient();
  const httpOnlyRefresh = isHttpOnlyRefreshEnabled();
  // Task #3065 — when the HttpOnly-cookie proxy is enabled, the
  // refresh token lives server-side in a cookie. The matching CSRF
  // token comes back in /adopt and /refresh responses; we keep it in
  // memory so subsequent /refresh + /logout calls can echo it back.
  const csrfRef = useRef<string | null>(null);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  useEffect(() => {
    bindAcquisition(session?.access_token || null);
  }, [session?.access_token, profile?.id]);
  const [isLoading, setIsLoading] = useState(true);
  const [profileError, setProfileError] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  // Task #4155 — effective (impersonated) host-status overlay. While an admin
  // impersonates a user, ImpersonationProvider fetches the impersonated user's
  // host status from /auth/effective-host-status (sending the impersonation
  // headers) and sets this. `null` means "no override" — the admin's own
  // `profile.is_host` shows through. This is intentionally kept as a separate
  // in-memory value (never written into the cached base profile) so starting
  // and stopping impersonation flips host status instantly with no cache bleed
  // in either direction.
  const [hostOverride, setHostOverride] = useState<boolean | null>(null);
  // Task #2451 — soft-deleted info surfaced from /auth/ensure-user when
  // the principal has no active local row but matches a soft-deleted
  // one inside the 30-day GDPR retention window.
  const [softDeletedInfo, setSoftDeletedInfo] = useState<SoftDeletedInfo | null>(null);
  // Task #5287 — true when the principal has authenticated but has no local
  // public.users row yet (web defers creation until the completion dialog
  // submits name + Terms). Distinct from profileError: this is an expected
  // mid-signup state, not a failure, and it mounts the completion dialog.
  const [needsProfile, setNeedsProfile] = useState(false);
  const identityVersion = useRef(0);
  const authPrincipal = useRef<string | null>(null);

  const fetchProfileViaApi = useCallback(
    async (accessToken: string, authUserId: string | null, retries = 2, opts?: { bypassCache?: boolean }): Promise<{ profile: UserProfile | null; is_owner: boolean; soft_deleted: SoftDeletedInfo | null; needs_profile: boolean; suspended?: boolean }> => {
      const version = identityVersion.current;
      const CACHE_TTL_MS = 5 * 60 * 1000;
      // User-scoped cache key: a single global key would let one user
      // (e.g. an admin you signed in as earlier in this tab) bleed their
      // cached profile into a brand-new signup that follows. That made
      // the ProfileCompletionDialog silently no-op for fresh OTP signups
      // because the cached `is_complete: true` overrode the new user's
      // real `is_complete: false`. If we don't yet know the auth user id
      // (defensive), skip the cache entirely.
      // Cache key is versioned (#1763). Pre-fix tabs may have a cached
      // `is_complete: undefined` snapshot (the ensure-user race-recovery
      // branch used to skip decorateProfile), which would keep a brand-
      // new user wedged behind a missing setup dialog even after the
      // server-side fix lands. Bumping the key to `_v2` invalidates
      // every pre-fix entry without requiring a sign-out.
      const cacheKey = authUserId ? `ensure_user_cache_v2:${authUserId}` : null;
      try {
        // Best-effort: drop legacy unscoped + pre-v2 cache keys from
        // previous versions so they can't be misread by anything else.
        sessionStorage.removeItem("ensure_user_cache");
        if (authUserId) {
          sessionStorage.removeItem(`ensure_user_cache:${authUserId}`);
        }
      } catch { /* noop */ }
      // `bypassCache` is set by retryProfile() — when ProfileCompletionDialog
      // (or any other completion flow) has just written to the user's row
      // it must read through to the server, otherwise the stale
      // `is_complete: false` snapshot in this user's per-user cache would
      // keep the dialog mounted and force a manual page refresh (#1752).
      if (opts?.bypassCache && cacheKey) {
        try { sessionStorage.removeItem(cacheKey); } catch { /* noop */ }
      }
      try {
        const raw = cacheKey && !opts?.bypassCache ? sessionStorage.getItem(cacheKey) : null;
        if (raw) {
          const cached = JSON.parse(raw);
          if (cached.ts && Date.now() - cached.ts < CACHE_TTL_MS && cached.profile) {
            const cachedProfile = cached.profile as UserProfile;
            const cachedIsOwner = !!cached.is_owner;
            const hydrated: UserProfile = {
              ...cachedProfile,
              is_host: typeof cachedProfile.is_host === "boolean" ? cachedProfile.is_host : cachedIsOwner,
            };
            return { profile: hydrated, is_owner: hydrated.is_host, soft_deleted: null, needs_profile: false };
          }
        }
      } catch {}

      for (let attempt = 0; attempt <= retries; attempt++) {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 12000);
        try {
          const config = getPlatformConfig();
          const apiBase = import.meta.env.BASE_URL || "/";
          // Drain any pending signup metadata (terms / marketing choices the
          // user made before email confirmation) so it gets recorded on the
          // very first authenticated profile call.
          let pendingMeta: { marketing_opt_in?: boolean; accept_terms?: boolean } = {};
          try {
            const raw = localStorage.getItem("auth_pending_signup_meta");
            if (raw) {
              const parsed = JSON.parse(raw);
              if (typeof parsed?.marketing_opt_in === "boolean") pendingMeta.marketing_opt_in = parsed.marketing_opt_in;
              if (parsed?.accept_terms === true) pendingMeta.accept_terms = true;
            }
          } catch { /* noop */ }
          const res = await fetch(`${apiBase}api/auth/ensure-user`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
            },
            // Task #5287 — WEB defers public.users creation until the
            // profile-completion dialog submits name + Terms. Tell ensure-user
            // not to auto-create: a no-row principal comes back as
            // account_status:"needs_profile" instead of a freshly-inserted row.
            body: JSON.stringify({ platform_origin: config.platform, defer_creation: true, ...pendingMeta }),
            signal: controller.signal,
          });
          if (version !== identityVersion.current) {
            clearTimeout(timeout);
            return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
          }
          if (res.ok && (pendingMeta.accept_terms || typeof pendingMeta.marketing_opt_in === "boolean")) {
            try { localStorage.removeItem("auth_pending_signup_meta"); } catch { /* noop */ }
          }
          clearTimeout(timeout);
          if (!res.ok) {
            console.warn(`[AuthProvider] ensure-user returned ${res.status} (attempt ${attempt + 1})`);
            const body = await res.json().catch(() => null) as { code?: unknown } | null;
            if (
              res.status === 409 &&
              ["existing_account_link_required", "identity_already_linked", "email_collision"].includes(
                typeof body?.code === "string" ? body.code : "",
              )
            ) {
              await Promise.allSettled([
                supabase?.auth.signOut({ scope: "local" }),
                proxyLogout(csrfRef.current),
              ]);
              return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
            }
            // 401 means the access token is invalid (e.g. the auth.users row
            // was deleted out from under this tab). Retrying won't help and
            // it leaves the tab in a zombie state where `user` is set but
            // `profile` is null forever, which makes the
            // ProfileCompletionWatcher silently no-op for any subsequent
            // signup. Force a clean signOut so the next signup starts from
            // a real fresh state.
            if (res.status === 401) {
              try { await supabase?.auth.signOut(); } catch { /* noop */ }
              return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
            }
            if (attempt < retries) {
              await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
              continue;
            }
            return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
          }
          const json = await res.json();
          if (version !== identityVersion.current) {
            return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
          }
          // Task #2451 — server returns account_status="soft_deleted" with
          // deletion timestamps in lieu of a profile. Surface it to the
          // caller so AuthProvider can render the restore CTA instead of
          // looping into profileError. We do NOT cache this — the user
          // is about to either restore (profile flips to active) or
          // sign out, both of which need a fresh read.
          if (json.account_status === "soft_deleted" && json.soft_deleted) {
            return {
              profile: null,
              is_owner: false,
              soft_deleted: {
                deletedAt: json.soft_deleted.deleted_at,
                scheduledErasureAt: json.soft_deleted.scheduled_erasure_at,
              },
              needs_profile: false,
            };
          }
          // Task #5287 — the principal has authenticated but has no local row
          // yet (deferred web signup). Surface needs_profile so AuthProvider
          // mounts the completion dialog WITHOUT flagging profileError. The row
          // is created by /auth/complete-profile once name + Terms are in.
          if (json.account_status === "needs_profile") {
            return { profile: null, is_owner: false, soft_deleted: null, needs_profile: true };
          }
          const rawProfile = (json.profile as UserProfile | null) ?? null;
          const isOwnerFlag = !!json.is_owner;
          const mergedProfile: UserProfile | null = rawProfile
            ? { ...rawProfile, is_host: typeof rawProfile.is_host === "boolean" ? rawProfile.is_host : isOwnerFlag }
            : null;
          // Task #5897 — suspended account. The server still 200s /auth/ensure-user
          // for a suspended EXISTING user (it returns the profile with
          // `is_active: false`), but every OTHER authed endpoint returns the
          // intended 403 `account_suspended`. Loading a half-usable "logged in"
          // state means all those background calls 403 (Sentry noise, now
          // dropped in shouldDropSentryEvent) and the user is shown nothing.
          // Surface the suspension gracefully instead: sign the session out
          // locally (stops further authed calls firing) and toast the intended
          // message. This is NOT reported as an error.
          if (mergedProfile && mergedProfile.is_active === false) {
            toast({
              title: i18n.t("auth.account_suspended_title"),
              description: i18n.t("auth.account_suspended_body"),
              variant: "destructive",
            });
            try { await supabase?.auth.signOut({ scope: "local" }); } catch { /* noop */ }
            return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false, suspended: true };
          }
          const result = { profile: mergedProfile, is_owner: mergedProfile ? mergedProfile.is_host : isOwnerFlag, soft_deleted: null, needs_profile: false };
          if (result.profile && cacheKey) {
            try {
              sessionStorage.setItem(cacheKey, JSON.stringify({ profile: result.profile, is_owner: result.is_owner, ts: Date.now() }));
            } catch {}
          }
          return result;
        } catch (err) {
          clearTimeout(timeout);
          console.warn(`[AuthProvider] ensure-user fetch error (attempt ${attempt + 1}):`, err);
          if (attempt < retries) {
            await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
            continue;
          }
          return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
        }
      }
      return { profile: null, is_owner: false, soft_deleted: null, needs_profile: false };
    },
    [],
  );

  const clearAuthState = useCallback((options?: { preserveAnonymousConciergeDrafts?: boolean }) => {
    identityVersion.current++;
    authPrincipal.current = null;
    setUser(null);
    setSession(null);
    setProfile(null);
    setProfileError(false);
    setIsOwner(false);
    setSoftDeletedInfo(null);
    setNeedsProfile(false);
    clearAllAuthStorage(
      options?.preserveAnonymousConciergeDrafts
        ? { preserveExactKeys: [...anonymousConciergeDraftKeysToPreserve(), ...anonymousCheckoutDraftKeysToPreserve()] }
        : undefined,
    );
    // Drop every cached query so the next user on a shared device cannot
    // read the previous user's bookings, payments, messages, etc. from
    // react-query's in-memory cache (task #1605). Cancels in-flight
    // queries too, which is important on logout because some background
    // refetches would otherwise resolve after the auth header is gone
    // and cache empty results into the wrong slot.
    try { queryClient.clear(); } catch { /* noop */ }
    Sentry.setUser(null);
    analyticsReset();
    shutdownIntercom();
  }, []);

  const retryProfile = useCallback(() => {
    const currentSession = session;
    if (!currentSession?.access_token) return;
    setProfileError(false);
    // Always read through to the server here. Callers (notably
    // ProfileCompletionDialog.onDone) invoke retryProfile *after* a
    // server-side mutation that flipped `is_complete`, so the cached
    // pre-mutation snapshot must not satisfy this fetch (#1752).
    fetchProfileViaApi(currentSession.access_token, currentSession.user?.id ?? null, 2, { bypassCache: true }).then(async (result) => {
      setProfile(result.profile);
      setSoftDeletedInfo(result.soft_deleted);
      setNeedsProfile(result.needs_profile);
      if (result.profile) {
        setProfileError(false);
        setIsOwner(result.is_owner);
      } else if (!result.soft_deleted && !result.needs_profile && !result.suspended) {
        setProfileError(true);
      } else {
        setProfileError(false);
      }
    }).catch(() => {
      setProfileError(true);
    });
  }, [session, fetchProfileViaApi]);

  // Optimistically merge a partial patch into the in-memory profile so
  // callers that persist a single field via a fire-and-forget PATCH
  // (e.g. useHostTour's `host_tour_seen_at`) don't have to wait for a
  // full profile refetch before the local state reflects the change.
  // No-ops when no profile is loaded.
  const patchProfile = useCallback((patch: Partial<UserProfile>) => {
    setProfile((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  const restoreAccount = useCallback(async () => {
    const currentSession = session;
    if (!currentSession?.access_token) return { error: new Error("Not signed in") };
    try {
      const apiBase = import.meta.env.BASE_URL || "/";
      const res = await fetch(`${apiBase}api/auth/restore-account`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentSession.access_token}`,
        },
      });
      if (!res.ok) {
        let msg = `Restore failed (${res.status})`;
        try { const j = await res.json(); if (j?.error) msg = j.error; } catch {}
        return { error: new Error(msg) };
      }
      setSoftDeletedInfo(null);
      // Re-fetch the profile fresh so AuthState flips to active.
      const result = await fetchProfileViaApi(currentSession.access_token, currentSession.user?.id ?? null, 2, { bypassCache: true });
      setProfile(result.profile);
      setSoftDeletedInfo(result.soft_deleted);
      setNeedsProfile(result.needs_profile);
      setIsOwner(result.is_owner);
      setProfileError(!result.profile && !result.soft_deleted && !result.needs_profile && !result.suspended);
      return { error: null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error("Restore failed") };
    }
  }, [session, fetchProfileViaApi]);

  // Task #3065 — periodic refresh timer when the proxy is enabled.
  // Supabase's autoRefreshToken is off; we ask the proxy for a fresh
  // access token a minute before expiry and push it into the client
  // via setSession with an inert refresh placeholder.
  //
  // IMPORTANT: this useCallback MUST be declared before the boot
  // useEffect below, because the boot effect references it (both in
  // hydrateFromProxy and in its dep array). Moving it after the
  // useEffect re-introduces a temporal-dead-zone ReferenceError:
  // "Cannot access 'scheduleProxyRefresh' before initialization".
  const scheduleProxyRefresh = useCallback(
    (expiresInSec: number) => {
      if (!httpOnlyRefresh || !supabase) return;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      // Refresh 60s before expiry, clamped to at least 30s out.
      const delayMs = Math.max(30_000, (expiresInSec - 60) * 1000);
      refreshTimerRef.current = setTimeout(async () => {
        const next = await refreshAccessToken(csrfRef.current);
        if (!next) {
          // Cookie is dead — sign out cleanly.
          const recoverInline = canRecoverInlineCheckout(window.location.pathname, window.location.search, authPrincipal.current ?? undefined);
          clearAuthState();
          if (!recoverInline) window.location.href = `${import.meta.env.BASE_URL || "/"}login?error=${encodeURIComponent("Session expired")}`;
          return;
        }
        if (next.csrf_token) csrfRef.current = next.csrf_token;
        try {
          const { data, error } = await supabase.auth.setSession({
            access_token: next.access_token,
            refresh_token: PROXY_REFRESH_PLACEHOLDER,
          });
          if (error || !data.session) throw error ?? new Error("Proxy session missing");
        } catch (err) {
          console.warn("[AuthProvider] proxy refresh setSession failed", err);
          clearAuthState();
          return;
        }
        scheduleProxyRefresh(next.expires_in);
      }, delayMs);
    },
    [httpOnlyRefresh, supabase, clearAuthState],
  );

  useEffect(() => {
    if (!supabase) {
      setIsLoading(false);
      return;
    }
    let mounted = true;
    let bootDone = false;
    let initialAnonymousSeen = false;
    const bootVersion = identityVersion.current;

    // Task #3065 — When the HttpOnly-cookie proxy is enabled, the
    // refresh token lives in a server-owned cookie, not in the
    // Supabase client. supabase.auth.getSession() will therefore
    // return null on cold load — we have to ask the proxy for a
    // fresh access token first, then push it into the client via
    // setSession so the rest of the component graph (Sentry tags,
    // ensure-user, intercom identity) keeps working unchanged.
    const hydrateFromProxy = async (): Promise<Session | null> => {
      if (!httpOnlyRefresh) return null;
      const authed = await probeSession();
      if (!authed) return null;
      const next = await refreshAccessToken(csrfRef.current);
      if (!next) return null;
      if (next.csrf_token) csrfRef.current = next.csrf_token;
      try {
        const { data, error } = await supabase.auth.setSession({
          access_token: next.access_token,
          refresh_token: PROXY_REFRESH_PLACEHOLDER,
        });
        if (error || !data.session) {
          console.warn("[AuthProvider] proxy hydration setSession failed", error);
          return null;
        }
        scheduleProxyRefresh(next.expires_in);
        return data.session;
      } catch (err) {
        console.warn("[AuthProvider] proxy hydration failed", err);
        return null;
      }
    };

    const boot = async (): Promise<{ data: { session: Session | null } }> => {
      // Task #5683 — a password-reset link lands with the recovery session in
      // the URL hash; `detectSessionInUrl` consumes it into the in-memory
      // Supabase client. In HttpOnly mode that session lives ONLY in memory
      // (persistSession is off). Running the proxy bootstrap here would
      // OVERWRITE the fresh recovery session with a stale/previous cookie
      // session (via `setSession` with a proxy placeholder) and arm a proxy
      // refresh timer that can later sign the tab out — leaving the
      // set-password submit to die with "Auth session missing". While a
      // recovery flow is active, skip proxy hydration and adopt the
      // URL-detected recovery session as-is (matching the non-proxy path).
      if (!isRecoveryMode()) {
        const proxied = await hydrateFromProxy();
        if (proxied) return { data: { session: proxied } };
      }
      const result = await supabase.auth.getSession();
      if (!httpOnlyRefresh && !result.data.session && !isRecoveryMode()) {
        return { data: { session: await restoreLiveTabSession() } };
      }
      return result;
    };

    // Task #6961 — auth-gated deep links (e.g. /trips/:id from a booking
    // email) render a PageLoader while `isLoading` is true. If the proxy
    // probe/refresh or getSession() hangs (dead mobile connection that never
    // errors), boot() never settles and the loader spins forever. Bound it:
    // past the timeout we proceed as signed-out so the page renders (an auth
    // redirect / sign-in prompt beats an infinite spinner). A late-resolving
    // proxy hydration still recovers via setSession → onAuthStateChange.
    const BOOT_TIMEOUT_MS = 10_000;
    let bootTimer: ReturnType<typeof setTimeout> | undefined;
    const bootWithTimeout = Promise.race([
      boot(),
      new Promise<{ data: { session: Session | null } }>((resolve) => {
        bootTimer = setTimeout(() => {
          console.warn("[AuthProvider] session boot timed out — rendering signed-out");
          resolve({ data: { session: null } });
        }, BOOT_TIMEOUT_MS);
      }),
    ]).finally(() => {
      if (bootTimer !== undefined) clearTimeout(bootTimer);
    });

    bootWithTimeout.then(async ({ data: { session: s } }) => {
      bootDone = true;
      if (!mounted || bootVersion !== identityVersion.current) return;
      if (s && !acceptsLiveTabSession(s)) return;
      authPrincipal.current = s?.user.id ?? null;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user && s.access_token) {
        if (getPlatformConfig().platform === "bac") analyticsIdentify(s.user.id, { email: s.user.email ?? undefined });
        const result = await fetchProfileViaApi(s.access_token, s.user.id);
        if (!mounted || bootVersion !== identityVersion.current) return;
        setProfile(result.profile);
        setSoftDeletedInfo(result.soft_deleted);
        setNeedsProfile(result.needs_profile);
        if (result.profile) {
          setProfileError(false);
          setIsOwner(result.is_owner);
        } else if (result.soft_deleted || result.needs_profile || result.suspended) {
          setProfileError(false);
        } else if (s?.user) {
          console.error("[AuthProvider] profile fetch failed after retries");
          setProfileError(true);
        }
        Sentry.setUser({ id: s.user.id, email: s.user.email });
        analyticsIdentify(s.user.id, {
          email: s.user.email ?? undefined,
          first_name: result.profile?.first_name ?? undefined,
          last_name: result.profile?.last_name ?? undefined,
          role: result.is_owner ? "owner" : "renter",
          locale: result.profile?.locale ?? undefined,
          currency: result.profile?.currency ?? undefined,
          signup_date: result.profile?.created_at ?? undefined,
        });
        setIsLoading(false);
        if (result.profile) {
          // Queue identified traits before /config/platform resolves;
          // bootIntercom replays them once configureIntercom sets the app id.
          const identity = await fetchIntercomIdentity(s.access_token);
          if (!mounted || bootVersion !== identityVersion.current) return;
          void bootIntercom(
            buildIntercomTraits(result.profile, s.user.email || undefined, result.is_owner, identity),
          );
        }
      }
      if (!s && initialAnonymousSeen) clearAuthState({ preserveAnonymousConciergeDrafts: true });
      setIsLoading(false);
    }).catch((err) => {
      console.error("[AuthProvider] getSession error:", err);
      setIsLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, s) => {
      // INITIAL_SESSION(null) is not a logout. Bootstrap must finish its
      // bounded live-tab restoration before clearing storage or opening login.
      if (event === "INITIAL_SESSION" && !s) {
        initialAnonymousSeen = true;
        if (bootDone) clearAuthState({ preserveAnonymousConciergeDrafts: true });
        return;
      }
      if (s && !acceptsLiveTabSession(s)) return;
      const version = ++identityVersion.current;
      // Never return an async SDK callback: getUser/refresh/setSession need
      // the SDK lock released before their continuations can run.
      void (async () => {
      if (event === "TOKEN_REFRESHED" && !s) {
        const recoverInline = canRecoverInlineCheckout(window.location.pathname, window.location.search, authPrincipal.current ?? undefined);
        clearAuthState();
        setIsLoading(false);
        if (!recoverInline) window.location.href = `${import.meta.env.BASE_URL || "/"}login?error=${encodeURIComponent("Session expired")}`;
        return;
      }

      if (event === "SIGNED_OUT") {
        clearAuthState();
        setIsLoading(false);
        return;
      }

      // Task #5620 — a recovery link was consumed into a session. Mark the
      // app in password-recovery mode so RecoveryModeRedirector routes the
      // user to the set-password form and PostAuthRedirector suppresses its
      // normal post-auth navigation. This is the secondary signal; the
      // primary one is the synchronous URL scan in the recoveryMode module
      // (survives this event firing before any listener subscribes).
      if (event === "PASSWORD_RECOVERY") {
        enterRecoveryMode();
      }

      // Belt-and-braces cache clear on sign-in: if the previous tab session
      // ended without SIGNED_OUT firing (e.g. crash, hard reload mid-flow)
      // the react-query cache could still hold the prior user's data when a
      // new user signs in on the same device. Clear it so the freshly
      // authenticated user always reads through to the server (task #1605).
      // Also nuke the sessionStorage `ensure_user_cache` — that key is global
      // (not user-scoped), so without this the freshly-signed-in user would
      // read the previous user's cached profile (incl. `is_complete: true`),
      // which causes the ProfileCompletionDialog to silently no-op for a
      // brand-new signup that follows an existing-user session in the same
      // tab.
      if (event === "SIGNED_IN") {
        if (authPrincipal.current && authPrincipal.current !== s?.user.id) {
          setProfile(null);
          setIsOwner(false);
          setSoftDeletedInfo(null);
          setNeedsProfile(false);
          for (const name of ["sessionStorage", "localStorage"] as const) {
            const store = authStorage(name);
            if (store) purgeStorageMatching(store, DRAFT_SIGNOUT_KEY_PREFIXES, []);
          }
        }
        try { queryClient.clear(); } catch { /* noop */ }
        // Drop EVERY `ensure_user_cache:*` key — including ones scoped to a
        // different (prior) auth UUID — so the freshly-signed-in user can
        // never read another user's cached profile snapshot in the same
        // tab (#1752, cross-user leak).
        //
        // IMPORTANT: use the SIGNED_IN-safe variant. The full-purge helper
        // also clears `sb-*` localStorage shards which Supabase JUST wrote
        // to persist this very session — wiping them here would log the
        // user back out on next reload (#1752 review feedback).
        clearUserScopedAppCaches();
      }

      authPrincipal.current = s?.user.id ?? null;
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user && s.access_token) {
        // Identify immediately on the existing anonymous PostHog session.
        // Profile hydration can take a round trip (and booking can be
        // submitted before it completes), so waiting for ensure-user here
        // would attribute the first booking event to the anonymous person.
        // analyticsIdentify keeps the current distinct_id and merges it into
        // this user; only clearAuthState/SIGNED_OUT calls analyticsReset().
        if (event === "SIGNED_IN") {
          analyticsIdentify(s.user.id, {
            email: s.user.email ?? undefined,
          });
        } else if (getPlatformConfig().platform === "bac") {
          // INITIAL_SESSION, refreshed/restored and callback-created sessions
          // must identify even if profile hydration is deferred or fails.
          analyticsIdentify(s.user.id, { email: s.user.email ?? undefined });
        }
        const result = await fetchProfileViaApi(s.access_token, s.user.id);
        if (!mounted || version !== identityVersion.current) return;
        setProfile(result.profile);
        setSoftDeletedInfo(result.soft_deleted);
        setNeedsProfile(result.needs_profile);
        if (result.profile) {
          setProfileError(false);
          setIsOwner(result.is_owner);
        } else if (result.soft_deleted || result.needs_profile || result.suspended) {
          setProfileError(false);
        } else {
          setProfileError(true);
        }
        Sentry.setUser({ id: s.user.id, email: s.user.email });
        analyticsIdentify(s.user.id, {
          email: s.user.email ?? undefined,
          first_name: result.profile?.first_name ?? undefined,
          last_name: result.profile?.last_name ?? undefined,
          role: result.is_owner ? "owner" : "renter",
        });
        setIsLoading(false);
        if (result.profile) {
          const identity = await fetchIntercomIdentity(s.access_token);
          if (!mounted || version !== identityVersion.current) return;
          void bootIntercom(
            buildIntercomTraits(result.profile, s.user.email || undefined, result.is_owner, identity),
          );
        }
      } else if (!s?.user) {
        clearAuthState(
          event === "INITIAL_SESSION" ? { preserveAnonymousConciergeDrafts: true } : undefined,
        );
      }
      if (mounted && version === identityVersion.current) setIsLoading(false);
      })().catch(() => {
        if (mounted && version === identityVersion.current) {
          setProfileError(true);
          setIsLoading(false);
        }
      });
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [supabase, fetchProfileViaApi, clearAuthState, httpOnlyRefresh, scheduleProxyRefresh]);

  // Task #6095 — account-level consent persistence. Once an authenticated
  // profile is loaded, wire the account consent layer: a pre-login browser
  // choice is pushed to the account, an account-stored choice is hydrated
  // into the browser (newer `ts` wins), and any subsequent banner choice is
  // written through to the account. This is what lets users whose browser
  // blocks ALL storage (iOS "Block All Cookies" / Lockdown Mode) stop
  // re-seeing the consent popup after every reload — the account is the only
  // durable store their device can't block.
  useEffect(() => {
    const token = session?.access_token;
    if (!token || !profile?.id) return;
    connectAccountConsent(token);
    return () => disconnectAccountConsent();
  }, [session?.access_token, profile?.id]);

  // Keep Intercom traits in sync with profile mutations (settings edits,
  // locale switch, currency change, host upgrade).
  useEffect(() => {
    if (!profile) return;
    updateIntercom(buildIntercomTraits(profile, user?.email || undefined, isOwner));
  }, [
    profile?.id,
    profile?.first_name,
    profile?.last_name,
    profile?.language,
    profile?.preferred_currency,
    user?.email,
    isOwner,
  ]);


  // Task #3065 — Adopt a fresh Supabase session into the HttpOnly
  // cookie, then strip the refresh token from the in-memory client
  // so it can never be written to localStorage. Idempotent: when the
  // flag is off it returns the session unchanged.
  // Task #3526 — return shape is `{ session, ok }`:
  //   - ok === true  → the refresh token is safely in the cookie (or the
  //     proxy is disabled, in which case Supabase's own localStorage
  //     refresh preserves the session's AAL and no cookie adoption is
  //     needed — either way the caller is safe to proceed),
  //   - ok === false → the proxy is enabled but adoption genuinely
  //     failed, so the cookie still holds the PREVIOUS refresh token.
  //     Callers elevating to aal2 MUST treat this as a hard failure and
  //     NOT drop the masking, otherwise the next proxy refresh re-mints
  //     from the stale aal1 token and silently downgrades the session.
  const adoptSessionIntoCookie = useCallback(
    async (s: Session | null): Promise<{ session: Session | null; ok: boolean }> => {
      // Proxy disabled: nothing to adopt. The in-memory Supabase client
      // keeps the refresh token and auto-refreshes at the same AAL.
      if (!httpOnlyRefresh) return { session: s, ok: true };
      if (!s || !supabase) return { session: s, ok: false };
      const refreshToken = s.refresh_token;
      const accessToken = s.access_token;
      if (!refreshToken || !accessToken) return { session: s, ok: false };
      try {
        const { csrf_token } = await adoptRefreshToken(refreshToken);
        csrfRef.current = csrf_token;
        // Re-seed the Supabase client with the access token only — no
        // refresh token in memory. This lets the rest of the app
        // continue calling supabase.auth.getUser() etc. without
        // exposing the refresh token to any code path.
        const { data, error } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: PROXY_REFRESH_PLACEHOLDER,
        });
        if (error || !data.session) throw error ?? new Error("Proxy session missing");
        return { session: data.session, ok: true };
      } catch (err) {
        console.warn("[AuthProvider] adoptSessionIntoCookie failed", err);
        return { session: s, ok: false };
      }
    },
    [httpOnlyRefresh, supabase],
  );

  const guestStartEpoch = useRef(0);
  const signOut = useCallback(async () => {
    if (!supabase) return;
    guestStartEpoch.current += 1;
    await import("@/lib/guest-checkout-api").then(({ endGuestCheckout }) => endGuestCheckout()).catch(() => {});
    const { clearAllCheckoutCustomerDrafts } = await import("@/lib/checkout-customer-draft");
    clearAllCheckoutCustomerDrafts();
    invalidateLiveTabSession();
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    // Drop the HttpOnly cookie server-side before we forget the CSRF
    // token. Best-effort — local state is cleared next regardless.
    if (httpOnlyRefresh) {
      await proxyLogout(csrfRef.current);
      csrfRef.current = null;
    }
    // Revoke the provider session while the SDK can still read its token.
    // Clearing storage first would leave booking-only delegated sessions alive.
    await supabase.auth.signOut({ scope: "local" }).catch((err) => {
      console.error("[AuthProvider] signOut error:", err instanceof Error ? err.name : "unknown");
      Sentry.captureException(err);
    });
    clearAuthState();
    // clearAuthState already calls clearAllAuthStorage(), which is now the
    // single authoritative purge — it removes `sb-*` localStorage tokens,
    // `auth_pending_signup_meta`, every `ensure_user_cache:*` entry, and
    // both redirect tokens. We deliberately do NOT duplicate that loop
    // here; if a new auth-owned key needs purging, register it in
    // AUTH_STORAGE_KEY_PREFIXES / AUTH_STORAGE_KEYS_EXACT instead.
    //
    // signOut({ scope: "local" }) is intentional and load-bearing — the
    // mobile (Expo) client at artifacts/mobile shares this contract; a
    // global scope would log the user out of every device, which the
    // web "Log out" button does not promise. See AUTH_FLOW.md.
    window.location.replace(import.meta.env.BASE_URL || "/");
  }, [supabase, clearAuthState]);

  const signOutForDriverInvitation = useCallback(
    async (continuationPath: string) => {
      if (!supabase) {
        throw new Error(i18n.t("booking.primary_driver.invitation_switch_failed"));
      }
      return completeDriverInvitationSignOut(continuationPath, {
        proxyLogout: async () => {
          if (!httpOnlyRefresh) return;
          await proxyLogoutStrict(csrfRef.current);
          csrfRef.current = null;
        },
        supabaseSignOut: async () => {
          const { error } = await supabase.auth.signOut({ scope: "local" });
          return { error };
        },
        clearAuthState,
        baseUrl: import.meta.env.BASE_URL || "/",
        reload: (loginPath) => window.location.replace(loginPath),
      });
    },
    [clearAuthState, httpOnlyRefresh, supabase],
  );

  const signInWithEmail = useCallback(
    async (email: string, password: string) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      // Task #3062 — go through our /auth/login proxy so the call is
      // rate-limited per-IP and per-email (Supabase's defaults are global
      // per-project and would otherwise let one noisy host lock everyone
      // out). The proxy returns the same session shape Supabase would
      // have set, which we hand to setSession so the SIGNED_IN listener
      // fires locally exactly as before. MFA detection then runs against
      // the live client below, unchanged.
      const proxyRes = await proxyAuthLogin(email, password);
      if (proxyRes.rateLimited) {
        return { error: rateLimitedAuthError(proxyRes.retryAfter) };
      }
      if (proxyRes.error || !proxyRes.session) {
        // Task #3890 (S-M7) — the server collapses every login failure to
        // one generic message; the only distinguishing signal it sends is
        // the `email_not_confirmed` code, which drives the resend-
        // verification flow. Carry it on the Error so the modal can branch
        // on the code instead of parsing leaked Supabase wording.
        const signInError = new Error(proxyRes.error || "Sign-in failed") as Error & { code?: string };
        if (proxyRes.code) signInError.code = proxyRes.code;
        return { error: signInError };
      }
      // The server already authenticated (proxy returned 200). The steps
      // below run purely against the in-memory Supabase client and acquire
      // the auth-token lock; if that lock is ever contended they could
      // hang, which is exactly what stranded the spinner. Each is bounded
      // by withTimeout as a safety net so a stalled call surfaces a
      // retryable error instead of spinning forever.
      let hydratedSession: Session | null = null;
      try {
        const { error: setErr } = await withTimeout(
          supabase.auth.setSession({
            access_token: proxyRes.session.access_token,
            refresh_token: proxyRes.session.refresh_token,
          }),
          AUTH_CLIENT_STEP_TIMEOUT_MS,
          "setSession",
        );
        if (setErr) {
          return { error: new Error(setErr.message) };
        }

        // Task #3065 — move the just-issued refresh token off the
        // Supabase client and into the HttpOnly cookie before any
        // further code path can observe it. Schedules the periodic
        // refresh so the access token doesn't expire mid-session.
        // The proxy returns just access/refresh strings, but
        // adoptSessionIntoCookie needs the full Supabase Session object
        // (user, token_type, …). The setSession() call above just
        // hydrated exactly that into the in-memory client, so read it
        // back instead of trying to reconstruct it from proxyRes.
        const { data: hydrated } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_CLIENT_STEP_TIMEOUT_MS,
          "getSession",
        );
        hydratedSession = hydrated.session;
      } catch (err) {
        console.warn("[AuthProvider] signInWithEmail stalled on local session step", err);
        return { error: stalledAuthClientError() };
      }

      if (hydratedSession) {
        try {
          const { session: adopted, ok } = await withTimeout(
            adoptSessionIntoCookie(hydratedSession),
            AUTH_CLIENT_STEP_TIMEOUT_MS,
            "adoptSessionIntoCookie",
          );
          if (ok && adopted) scheduleProxyRefresh(adopted.expires_in ?? 3600);
        } catch (err) {
          // Non-fatal: the access token is live in the in-memory client, so
          // the user is signed in. Cookie adoption / periodic refresh just
          // didn't get scheduled. Don't block the sign-in on this — the
          // existing adopt-failure path is already non-fatal (ok === false).
          console.warn("[AuthProvider] signInWithEmail adopt step stalled", err);
        }
      }

      // Login establishes an aal1 session. Two-factor is enforced at the
      // admin panel (AdminMfaGate + server requireAdmin), not at login.
      return { error: null };
    },
    [supabase],
  );

  // Task #3526 — in-place admin step-up. Used by AdminMfaGate when an
  // already-enrolled admin lands on the gate at aal1 (login establishes an
  // aal1 session; two-factor is enforced here at the admin panel, not at
  // login). It challenges the user's verified TOTP factor, verifies the
  // supplied code, and adopts the resulting aal2 refresh token into the
  // HttpOnly cookie so subsequent proxy refreshes keep aal2.
  //
  // The challenge+verify happen in a single submit, so there is no pending
  // challenge state to stash. On success it returns the elevated access
  // token so the caller can re-check /auth/mfa/status immediately, without
  // waiting for the AuthProvider session state to round-trip through
  // onAuthStateChange.
  const stepUpAdminSession = useCallback(
    async (code: string): Promise<{ error: Error | null; accessToken?: string }> => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      const { data: factors, error: listError } = await supabase.auth.mfa.listFactors();
      if (listError) return { error: new Error(listError.message) };
      const totpFactor = factors?.totp?.[0];
      if (!totpFactor) {
        return { error: new Error("No authenticator app is enrolled on this account.") };
      }
      const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });
      if (challengeError) return { error: new Error(challengeError.message) };
      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challenge.id,
        code,
      });
      if (verifyError) return { error: new Error(verifyError.message) };

      let fresh: Session | null;
      try {
        const { data: hydrated } = await withTimeout(
          supabase.auth.getSession(),
          AUTH_CLIENT_STEP_TIMEOUT_MS,
          "getSession",
        );
        fresh = hydrated.session;
      } catch (err) {
        console.warn("[AuthProvider] stepUpAdminSession stalled reading session", err);
        return { error: new Error("Couldn't read the elevated session. Please try again.") };
      }
      if (!fresh) {
        return { error: new Error("Couldn't read the elevated session. Please try again.") };
      }
      if (httpOnlyRefresh && decodeAccessTokenAal(fresh.access_token) !== "aal2") {
        return { error: new Error("Two-factor verification didn't elevate your session. Please try again.") };
      }
      let adoptedOk = false;
      let adoptedSession: Session | null = fresh;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const { session: adopted, ok } = await withTimeout(
            adoptSessionIntoCookie(fresh),
            AUTH_CLIENT_STEP_TIMEOUT_MS,
            "adoptSessionIntoCookie",
          );
          if (ok) {
            adoptedOk = true;
            adoptedSession = adopted;
            break;
          }
        } catch (err) {
          // Bounded — a stalled adopt attempt counts as a failed attempt so
          // the loop can retry / fall through to the error below instead of
          // hanging the step-up flow.
          console.warn("[AuthProvider] stepUpAdminSession adopt attempt stalled", err);
        }
      }
      if (!adoptedOk) {
        return { error: otpSessionSetupError() };
      }
      if (adoptedSession) scheduleProxyRefresh(adoptedSession.expires_in ?? 3600);
      return { error: null, accessToken: fresh.access_token };
    },
    [supabase, httpOnlyRefresh, adoptSessionIntoCookie, scheduleProxyRefresh],
  );

  const signUpWithEmail = useCallback(
    async (email: string, password: string, metadata?: Record<string, string>, next?: string) => {
      if (!supabase) return { error: new Error("Supabase not configured"), needsConfirmation: false };
      // Build the same callback URL as the magic-link / OAuth paths so the
      // confirmation email's `{{ .ConfirmationURL }}` embeds *our* domain
      // (PKCE exchange happens at /auth/callback). Without this Supabase
      // falls back to the dashboard Site URL — which has historically
      // pointed at localhost in dev — and the link is unusable.
      const callbackUrl = new URL(`${window.location.origin}${import.meta.env.BASE_URL}auth/callback`);
      const safeNext = safeRedirectPath(next);
      if (safeNext !== "/") callbackUrl.searchParams.set("next", safeNext);
      // Task #3062 — proxy through /auth/signup so the per-IP cap fires
      // before Supabase ever sees the request. The proxy returns the same
      // {user, session} shape signUp produces; if a session is returned
      // (auto-confirm enabled) we install it locally so the SIGNED_IN
      // listener fires as if the client called Supabase directly.
      const proxyRes = await proxyAuthSignup(email, password, metadata, callbackUrl.toString());
      if (proxyRes.rateLimited) {
        return { error: rateLimitedAuthError(proxyRes.retryAfter), needsConfirmation: false };
      }
      const error = proxyRes.error
        ? Object.assign(new Error(proxyRes.error), { code: proxyRes.code })
        : null;
      const data = { user: proxyRes.user, session: proxyRes.session } as { user: User | null; session: Session | null };
      if (data.session) {
        await supabase.auth.setSession({
          access_token: data.session.access_token,
          refresh_token: data.session.refresh_token,
        }).catch(() => undefined);
      }
      if (error) {
        // Supabase sometimes returns an explicit "user already registered" error
        // (e.g. when "Confirm email" is disabled). Tag it so the UI can route
        // the user back to the login step instead of showing a raw message.
        const code = (error as unknown as { code?: string }).code;
        const msg = (error.message || "").toLowerCase();
        const isDup = code === "user_already_exists"
          || code === "email_exists"
          || msg.includes("already registered")
          || msg.includes("already exists");
        const out = new Error(error.message) as Error & { code?: string; duplicate?: boolean };
        if (isDup) {
          out.duplicate = true;
          out.code = "user_already_exists";
        }
        return { error: out, needsConfirmation: false };
      }
      // Supabase silently no-ops a duplicate sign-up when "Confirm email" is
      // enabled: it returns a fake user with no identities and no session,
      // and DOES NOT actually send a verification email. Detect that here so
      // the UI can route the user to log in instead of showing a misleading
      // "check your email" screen.
      const identities = (data.user as { identities?: unknown[] } | null)?.identities;
      if (data.user && Array.isArray(identities) && identities.length === 0) {
        const dup = new Error("An account with this email already exists.") as Error & { code?: string; duplicate?: boolean };
        dup.duplicate = true;
        dup.code = "user_already_exists";
        return { error: dup, needsConfirmation: false };
      }
      const needsConfirmation = !data.session;
      return { error: null, needsConfirmation };
    },
    [supabase],
  );

  const signInWithOAuth = useCallback(
    async (provider: "google" | "facebook" | "apple", next?: string) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      const callbackUrl = webOAuthCallbackUrl(window.location.origin, import.meta.env.BASE_URL, next);
      const providerOptions: { scopes?: string; queryParams?: Record<string, string> } = {};
      if (provider === "google") {
        providerOptions.scopes = "email profile";
        providerOptions.queryParams = { access_type: "offline", prompt: "select_account" };
      } else if (provider === "facebook") {
        providerOptions.scopes = "email public_profile";
      } else if (provider === "apple") {
        providerOptions.scopes = "email name";
      }
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: callbackUrl.toString(),
          ...providerOptions,
        },
      });
      return { error: error ? new Error(error.message) : null };
    },
    [supabase],
  );

  const connectApple = useCallback(async () => {
    if (!supabase) return { error: new Error("Supabase not configured") };
    const currentSession = session;
    if (!currentSession?.access_token || !currentSession.user) {
      return { error: new Error("You must be signed in to connect Apple") };
    }
    if (currentSession.user.identities?.some((identity) => identity.provider === "apple")) {
      return { error: null };
    }

    try {
      const apiBase = import.meta.env.BASE_URL || "/";
      // The server signs and allow-lists this target. Do not accept a caller
      // supplied redirect here: linking always returns to Account Security.
      const intentResponse = await fetch(`${apiBase}api/auth/apple-link-intent`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${currentSession.access_token}`,
        },
        body: JSON.stringify({ return_to: "/settings?section=security" }),
      });
      const intentBody = await intentResponse.json().catch(() => null) as { intent?: unknown; error?: unknown } | null;
      if (!intentResponse.ok || typeof intentBody?.intent !== "string") {
        return { error: new Error(typeof intentBody?.error === "string" ? intentBody.error : "Unable to start Apple linking") };
      }

      const callbackUrl = new URL(`${window.location.origin}${import.meta.env.BASE_URL}auth/callback`);
      // This opaque, server-signed value is the only linking state permitted
      // through the provider callback. The return target is inside the intent.
      callbackUrl.searchParams.set("apple_link_intent", intentBody.intent);
      const { error } = await supabase.auth.linkIdentity({
        provider: "apple",
        options: { redirectTo: callbackUrl.toString(), scopes: "email name" },
      });
      return { error: error ? new Error(error.message) : null };
    } catch (err) {
      return { error: err instanceof Error ? err : new Error("Unable to start Apple linking") };
    }
  }, [session, supabase]);

  const connectGoogle = useCallback(async () => {
    if (!supabase) return { error: new Error("Supabase not configured") };
    if (!session?.user) return { error: new Error("You must be signed in to connect Google") };
    if (session.user.identities?.some((identity) => identity.provider === "google")) {
      return { error: null };
    }

    const callbackUrl = new URL(`${window.location.origin}${import.meta.env.BASE_URL}auth/callback`);
    callbackUrl.searchParams.set("next", "/settings?section=security");
    const { error } = await supabase.auth.linkIdentity({
      provider: "google",
      options: {
        redirectTo: callbackUrl.toString(),
        scopes: "email profile",
        queryParams: { access_type: "offline", prompt: "select_account" },
      },
    });
    return { error: error ? new Error(error.message) : null };
  }, [session, supabase]);

  const signInWithOtp = useCallback(
    async (
      email: string,
      next?: string,
      opts?: {
        force?: boolean;
        journey?: import("@/lib/auth/otp-journey").OtpJourneyMetadata;
        testDelivery?: boolean;
      },
    ) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      const callbackUrl = new URL(`${window.location.origin}${import.meta.env.BASE_URL}auth/callback`);
      const safeNext = safeRedirectPath(next);
      if (safeNext !== "/") callbackUrl.searchParams.set("next", safeNext);
      // Task #3062 — proxy through /auth/request-otp so the call is
      // rate-limited per-email + per-IP by us, not just by Supabase's
      // global per-project default. The proxy then performs the same
      // signInWithOtp call server-side.
      const proxyRes = await proxyAuthRequestOtp(email, callbackUrl.toString(), opts);
      if (proxyRes.rateLimited) {
        return { error: rateLimitedAuthError(proxyRes.retryAfter) };
      }
      if (proxyRes.error) {
        // Preserve the proxy's failure classification on the Error so
        // AuthModal can pick the right copy. This includes "unavailable":
        // limiter infrastructure outages are not real rate limits and must
        // remain immediately retryable instead of becoming a lockout.
        const err = new Error(proxyRes.error) as Error & { code?: string };
        if (proxyRes.code) err.code = proxyRes.code;
        return { error: err };
      }
      // Task #6311 — the server deduped this request because a still-valid
      // code went out moments ago. Not an error: the modal renders it as
      // "we already sent you a code" guidance.
      if (proxyRes.alreadySent) {
        return {
          error: null,
          alreadySent: true,
          retryAfterSec: proxyRes.retryAfterSec,
          otpJourneyProof: proxyRes.otpJourneyProof,
          otpTestReceipt: proxyRes.otpTestReceipt,
        };
      }
      return {
        error: null,
        otpJourneyProof: proxyRes.otpJourneyProof,
        otpTestReceipt: proxyRes.otpTestReceipt,
      };
    },
    [supabase],
  );

  const startGuestCheckout = useCallback(async (details: Parameters<typeof beginGuestCheckout>[0]) => {
    if (!supabase) return { error: new Error("Please try again.") };
    const epoch = ++guestStartEpoch.current;
    try {
      const before = (await supabase.auth.getSession()).data.session?.user.id;
      const result = await withTimeout(beginGuestCheckout(details), AUTH_CLIENT_STEP_TIMEOUT_MS, "Guest booking");
      if (epoch !== guestStartEpoch.current) {
        await import("@/lib/guest-checkout-api").then(({ endGuestCheckout }) => endGuestCheckout(details.flowId)).catch(() => {});
        return { error: new Error("Please continue again.") };
      }
      const live = (await supabase.auth.getSession()).data.session;
      if (live?.user.id !== before) return { error: new Error("Your sign-in changed. Please review your booking.") };
      if (httpOnlyRefresh) {
        const adopted = await withTimeout(adoptSessionIntoCookie(result.session), AUTH_CLIENT_STEP_TIMEOUT_MS, "Guest session");
        if (!adopted.ok || !adopted.session) return { error: new Error("We couldn't continue. Please try again.") };
        const latest = (await supabase.auth.getSession()).data.session;
        if (latest?.user.id !== before) {
          if (latest) await adoptSessionIntoCookie(latest);
          else await proxyLogout(csrfRef.current);
          return { error: new Error("Your sign-in changed. Please review your booking.") };
        }
        scheduleProxyRefresh(adopted.session.expires_in ?? 3600);
      }
      if (epoch !== guestStartEpoch.current) {
        await import("@/lib/guest-checkout-api").then(({ endGuestCheckout }) => endGuestCheckout(details.flowId)).catch(() => {});
        return { error: new Error("Please continue again.") };
      }
      const signedIn = await supabase.auth.setSession({
        access_token: result.session.access_token, refresh_token: result.session.refresh_token,
      });
      return { error: signedIn.error ? new Error("We couldn't continue. Please try again.") : null,
        authUserId: signedIn.error ? undefined : result.session.user.id };
    } catch {
      return { error: new Error("We couldn't continue. Please try again.") };
    }
  }, [supabase, httpOnlyRefresh, adoptSessionIntoCookie, scheduleProxyRefresh]);

  const verifyEmailOtp = useCallback(
    async (
      email: string,
      token: string,
      opts?: {
        journey?: import("@/lib/auth/otp-journey").OtpJourneyMetadata;
        journeyProof?: string;
        testMagicLinkTokenHash?: string;
      },
    ) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      const cleaned = token.replace(/\D/g, "");
      const { data, error } =
        import.meta.env.DEV && opts?.testMagicLinkTokenHash
          ? await supabase.auth.verifyOtp({
              token_hash: opts.testMagicLinkTokenHash,
              type: "magiclink",
            })
          : await supabase.auth.verifyOtp({
              email,
              token: cleaned,
              type: "email",
            });
      if (error) {
        return { error: new Error(error.message) };
      }
      // OTP verification establishes a fresh session just like password or
      // OAuth sign-in. In HttpOnly mode the refresh token must be adopted into
      // the proxy cookie before the UI proceeds; otherwise the invitation page
      // works until reload and then appears signed out.
      if (httpOnlyRefresh) {
        try {
          if (!data.session) {
            throw otpSessionSetupError();
          }
          const { session: adoptedSession, ok } = await withTimeout(
            adoptSessionIntoCookie(data.session),
            AUTH_CLIENT_STEP_TIMEOUT_MS,
            "OTP session cookie adoption",
          );
          if (!ok || !adoptedSession) {
            throw otpSessionSetupError();
          }
          scheduleProxyRefresh(adoptedSession.expires_in ?? 3600);
        } catch (err) {
          // Do not leave an in-memory-only session that AuthModal could mistake
          // for a successful OTP race. A failed adoption must remain retryable
          // and must not strand a driver invitation after a reload.
          console.warn("[AuthProvider] OTP session cookie adoption failed", err);
          await Promise.allSettled([
            supabase.auth.signOut({ scope: "local" }),
            proxyLogout(csrfRef.current),
          ]);
          csrfRef.current = null;
          clearAuthState();
          return {
            error: otpSessionSetupError(),
          };
        }
      }
      if (opts?.journey && opts.journeyProof && data.session?.access_token) {
        void reportOtpVerificationComplete(data.session.access_token, opts.journey, opts.journeyProof);
      }
      // Login establishes an aal1 session. Two-factor is enforced at the
      // admin panel (AdminMfaGate + server requireAdmin), not at login.
      return { error: null };
    },
    [
      supabase,
      httpOnlyRefresh,
      adoptSessionIntoCookie,
      scheduleProxyRefresh,
      clearAuthState,
    ],
  );

  const resetPassword = useCallback(
    async (email: string) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      // Task #3062 — proxy through /auth/password-reset so we apply our
      // own per-email + per-IP cap (Supabase's per-project default would
      // otherwise let a single attacker spam any victim's inbox at the
      // platform-wide quota). The proxy returns a uniform `{ ok: true }`
      // regardless of whether the address exists, so we surface a
      // null-error success and let the UI render its uniform "if that
      // account exists, we've sent a reset" message.
      const proxyRes = await proxyAuthPasswordReset(
        email,
        `${window.location.origin}${import.meta.env.BASE_URL}reset-password`,
      );
      if (proxyRes.rateLimited) {
        return { error: rateLimitedAuthError(proxyRes.retryAfter) };
      }
      if (proxyRes.error) {
        // Same transport-vs-supabase propagation as signInWithOtp — so
        // a cold-start 502 during password reset surfaces the transient
        // copy in the modal rather than the ambiguous fallback.
        const err = new Error(proxyRes.error) as Error & { code?: string };
        if (proxyRes.code) err.code = proxyRes.code;
        return { error: err };
      }
      return { error: null };
    },
    [supabase],
  );

  const updatePassword = useCallback(
    async (password: string) => {
      if (!supabase) return { error: new Error("Supabase not configured") };
      const { error } = await supabase.auth.updateUser({ password });
      return { error: error ? new Error(error.message) : null };
    },
    [supabase],
  );

  // Admin-identity fields are always sourced from the REAL admin's base
  // profile — never the impersonated overlay below. ImpersonationProvider
  // clears the session when admin_role drops below its threshold, so reading
  // these off the host's (admin_role 0) profile would make impersonation
  // self-clear and strip the admin's controls.
  const adminRole = profile?.admin_role ?? 0;
  const permissions = profile?.permissions ?? [];

  // Task #4155 — overlay ONLY `is_host` from the effective (impersonated) host
  // status while impersonating. Everything else (incl. the admin-identity
  // fields above) stays sourced from the admin's own base profile.
  const effectiveProfile = useMemo<UserProfile | null>(() => {
    if (!profile || hostOverride === null) return profile;
    if (profile.is_host === hostOverride) return profile;
    return { ...profile, is_host: hostOverride };
  }, [profile, hostOverride]);

  const value: AuthState = {
    user,
    session,
    profile: effectiveProfile,
    isLoading,
    profileError,
    softDeletedInfo,
    needsProfile,
    restoreAccount,
    patchProfile,
    setHostOverride,
    isAdmin: adminRole > 0,
    adminRole,
    permissions,
    // Task #4536 — tier-3 super_admin is a wildcard: it holds every
    // permission implicitly, mirroring the backend's super-admin bypass.
    // Without this, a fresh permission string (e.g. fraud clear/cancel) gates
    // out super_admins until their explicit grant list is synced.
    hasPermission: (perm: string) => adminRole >= 3 || permissions.includes(perm),
    isOwner,
    retryProfile,
    signOut,
    signOutForDriverInvitation,
    signInWithEmail,
    stepUpAdminSession,
    signUpWithEmail,
    signInWithOAuth,
    connectApple,
    connectGoogle,
    signInWithOtp,
    verifyEmailOtp,
    startGuestCheckout,
    resetPassword,
    updatePassword,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
